import { Form } from "antd"
import { Alert, DateFormat, Flex, Group, Icon, Legend, SelectInput, Text, TextInput } from "components/common"
import { t } from "i18next"
import { useCallback, useEffect, useMemo, useState } from "react"

import cx from "clsx"
import { configs } from "configs"
import dayjs from "dayjs"
import { useLazyGetEventsForCalendarQuery } from "features/calendar/api"
import { schemaRules } from "features/shared/utils"
import { getAddTaskInitialValues } from "./addTaskInitialValues"
import getTaskValidationSchema from "./addTaskValidationSchema"
import styles from "./index.module.less"
import MeetingVerificationAttachmentUploader from "./meetingVerificationAttachmentUploader"

import VoiceComment from "features/tasks/components/voiceComment/voiceComment"
import NextTask from "../nextTask"
import {
  buildTaskTypeParams,
  filterSubTaskchildren,
  getAlert,
  getSubTasks,
  isAttachmentVerificationRequired,
  isMeetingAttachmentMandatory,
  isProposalOrVideoMeetingOrWalkIn,
  isRenderMeetingDateTime,
  isSubTaskSalesEvent,
  renderSuggestedComments,
  isPDFAllowed,
} from "../utilis"
import DateTimePicker from "../dateTimePicker"
import SubTaskAdditionalFields from "../subTaskAdditionalFields"
import { getMeetingTimeForDayInEvent, getSaleEventWindowMeta } from "../saleEventMeetingDoneUtils"
import { useLazyGetTaskTypeQuery } from "features/shared/api"
import { useSelector } from "react-redux"
import getPrivilege from "utils/privileges/privileges"
import useAgencyConfigurations from "hooks/useAgencyConfigurations"

export default function AddTaskForm({
  lead,
  isEditable,
  form,
  leadDetailInfo,
  setBusySlotVisible,
  busySlotVisible,
  formRef,
  onSubmit = () => {},
  recorderControls,
  setIsAttachmentValidation,
  client
}) {
  const userInfo = useSelector(state => state?.app?.user.info)
  const userPrivileges = useSelector(state => state?.app?.privileges)

  const [getTaskTypes, { data: taskTypes, isFetching: isFetchingTaskTypes }] = useLazyGetTaskTypeQuery()
  const [getSalesEvents, { data: salesEvents, isFetching: isFetchingsSaleEvents }] = useLazyGetEventsForCalendarQuery()

  const rules = schemaRules(getTaskValidationSchema())
  const { setFieldsValue } = form
  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form) || null
  const subofSubTask = Form.useWatch("subofSubTask", form)
  const subLocation = Form.useWatch("subLocation", form)
  const attachment = Form.useWatch("attachment", form)
  const userPlatformKey = useAgencyConfigurations()?.Platform?.platformKey
  const clientId = client?.id || ""

  const [saleEventDateBounds, setSaleEventDateBounds] = useState(null)

  const taskChildren = useMemo(() => {
    return getSubTasks(taskTypes, task, leadDetailInfo)
  }, [task, leadDetailInfo?.isTaskInQCState, taskTypes])

  const taskSubChildren = useMemo(() => {
    const subchildren = taskChildren?.subTasksMap?.[subTask]
    return filterSubTaskchildren(subchildren, salesEvents)
  }, [subTask, taskChildren, salesEvents])

  const isMeetingDoneAtSaleEvent = task === "meeting" && subTask === "meeting_done" && subofSubTask === "sale_event"

  const getSalesEventsData = useCallback(
    (item, subTaskParam) => {
      const date = item ? dayjs(item).toISOString() : dayjs().toISOString()
      const effectiveSubTask = !!subTaskParam ? subTaskParam : subTask
      const includeClientIdForSaleEvents = isMeetingDoneAtSaleEvent
        
      getSalesEvents(
        {
          afterTime: date,
          eventTypes: "sale",
          subTask: effectiveSubTask,
          platform: userPlatformKey,
          ...(includeClientIdForSaleEvents && {
            clientId,
          }),
        },
        true
      )
        .unwrap()
        .then(response => {
          if (response?.salesEventKeysMap?.[leadDetailInfo?.salesEventKey]) {
            setFieldsValue({ salesEvent: leadDetailInfo?.salesEventKey })
          }
        })
    },
    [
      task,
      subTask,
      subofSubTask,
      taskSubChildren,
      clientId,
      userPlatformKey,
      isEditable,
      isMeetingDoneAtSaleEvent,
    ]
  )

  useEffect(() => {
    if (formRef && leadDetailInfo) {
      getTaskTypes({
        params: buildTaskTypeParams({ userInfo, lead, isEditable, leadDetailInfo }),
      })
    }
  }, [formRef, leadDetailInfo])

  useEffect(() => {
    if (formRef && leadDetailInfo) {
      const passSubTask = task === "meeting" && subTask !== "video_meeting" && subTask !== "walk_in" ? subTask : null
      getSalesEventsData("", passSubTask)
    }
  }, [formRef, leadDetailInfo, subTask])

  const setFormValues = () => {
    form.setFieldsValue(getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project, isEditable: isEditable }))
  }

  useEffect(() => {
    if (leadDetailInfo) {
      setFormValues()
    }
  }, [leadDetailInfo, lead])



  const completionDateRange = useMemo(() => {
    if (saleEventDateBounds) {
      return {
        startDate: dayjs(saleEventDateBounds.start).startOf("day"),
        endDate: dayjs(saleEventDateBounds.end).endOf("day"),
      }
    }
    return {
      endDate: dayjs().add(0, "days"),
      startDate: dayjs()
        .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
        .startOf("day"),
    }
  }, [saleEventDateBounds])

  const meetingDatePickerProps = useMemo(() => {
    return {
      disabled: isEditable,
      onSelect: item => {
        if (dayjs(item).isSame(dayjs(), "day")) {
          if (item > dayjs()) {
            setFieldsValue({ completionDate: dayjs() })
          }
        }
      },
      onChange: item => {
        const passSubTask = task === "meeting" && subTask !== "video_meeting" && subTask !== "walk_in" ? subTask : null
        getSalesEventsData(item, passSubTask)
        if (isMeetingDoneAtSaleEvent && saleEventDateBounds?.isMultiDay && item) {
          const [st, en] = getMeetingTimeForDayInEvent(item, saleEventDateBounds.start, saleEventDateBounds.end)
          setFieldsValue({ meetingTime: [st, en] })
        }
      },
      disableFutureTime: true,
      ...(isMeetingDoneAtSaleEvent &&
        saleEventDateBounds?.isMultiDay && {
          disabledDate: current => {
            if (!current) return false
            const s = dayjs(saleEventDateBounds.start).startOf("day")
            const e = dayjs(saleEventDateBounds.end).startOf("day")
            return current.isBefore(s, "day") || current.isAfter(e, "day")
          },
        }),
    }
  }, [
    isEditable,
    task,
    subTask,
    getSalesEventsData,
    isMeetingDoneAtSaleEvent,
    saleEventDateBounds,
  ])

  const subLocations = useMemo(
    () => taskSubChildren?.find(e => e?.key === subofSubTask)?.children,
    [subofSubTask, taskSubChildren]
  )

  const resetMeetingTimeToDefault = (taskSubChildrenList, currentSubTask, locationValue) => {
    if (isSubTaskSalesEvent(taskSubChildrenList, locationValue) || !isRenderMeetingDateTime(currentSubTask)) {
      return
    }
    const completionDate = form.getFieldValue("completionDate") || dayjs()
    const baseDate = completionDate.format("YYYY-MM-DD")
    const now = dayjs()
    const nearestMinutes = Math.ceil(now.minute() / 15) * 15
    const startTimeStr = now.minute(nearestMinutes).second(0).millisecond(0).format("HH:mm")
    const endTimeStr = dayjs(startTimeStr, "HH:mm").add(60, "minute").format("HH:mm")
    const defaultStart = dayjs(`${baseDate} ${startTimeStr}`, "YYYY-MM-DD HH:mm")
    const defaultEnd = dayjs(`${baseDate} ${endTimeStr}`, "YYYY-MM-DD HH:mm")
    form.setFieldsValue({ meetingTime: [defaultStart, defaultEnd] })
  }

  const meetingVerificationTaskTypeKey = useMemo(() => {
    const hasSubLocations = subLocations?.length > 0
    if (subTask === "walk_in") return "walk_in"
    if (subTask === "video_meeting") return subofSubTask || "video_meeting"
    if (subTask === "meeting_done") {
      return hasSubLocations ? subLocation || subofSubTask || "sale_event" : (subofSubTask ?? null)
    }
    return null
  }, [subTask, subofSubTask, subLocation, subLocations?.length])

  const canAddMeetingWithoutAttachment = getPrivilege("can_add_meeting_without_attachment", userPrivileges)
  const isAttachmentMandatory = isProposalOrVideoMeetingOrWalkIn(subTask, subofSubTask) ||
      isMeetingAttachmentMandatory(subTask, canAddMeetingWithoutAttachment)
  return (
    <Form
      form={form}
      layout="vertical"
      name="task-form"
      initialValues={getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project })}
      onFinish={values =>
        onSubmit({
          values: values,
          taskType: taskTypes?.taskKeyNameMap?.[values?.task],
          nextTask: taskTypes?.nextTaskKeyName?.[values?.nextTask],
        })
      }
      scrollToFirstError
      preserve={false}
    >
      <Group>
        <div>
          {getAlert(leadDetailInfo)}
          {isEditable && leadDetailInfo?.isTaskStatusSales && (
            <Alert message={t("Sales Task Cannot Be Edited")} type="error" showIcon className="mbe-4" />
          )}

          <Legend title={t("Current Task")} />
          <Group template="repeat(2, 1fr)" gap="6px 32px">
            <Form.Item name="task" label={t("Task")} rules={rules} required>
              <SelectInput
                placeholder={t("Select task")}
                options={taskTypes?.leadStatuses}
                inputLoading={isFetchingTaskTypes}
                getOptionValue={option => option?.key}
                onChange={(value, option) => {
                  setFieldsValue({
                    task: value,
                    subTask: option.subTask[0]?.key,
                    nextTask: (option.subTask[0].nextStatus || option.nextTask)?.key,
                  })
                }}
                disabled={isEditable}
              />
            </Form.Item>
            {!!taskChildren?.options?.length && (
              <Form.Item
                name="subTask"
                label={t("Sub-Task")}
                rules={rules.concat({
                  validator: async (_, value) => {
                    if (value === "meeting_pushed" && !!leadDetailInfo?.isTaskInQCState) {
                      return Promise.reject("Cannot add task, in QC state")
                    }
                    return Promise.resolve()
                  },
                })}
                required
              >
                <SelectInput
                  placeholder={t("Select sub task")}
                  options={taskChildren?.options}
                  getOptionValue={option => option?.key}
                  onChange={(value, option) => {
                    if (value !== "meeting_done") {
                          setSaleEventDateBounds(null)
                    }
                    setFieldsValue({
                      subofSubTask: null,
                      subTask: value,
                      nextTask: option.nextStatus?.key ? option.nextStatus?.key : "do_nothing",
                      attachment: [],
                    })
                    const saleEventClient = leadDetailInfo?.inquiry?.Client?.SalesEventClients?.find(item =>
                      ["checked_in", "in_meeting"]?.includes(item?.SalesEventClientStatus?.key)
                    )
                    if (value === "meeting_done" && saleEventClient?.SalesEvent) {
                      const subchildren = taskChildren?.subTasksMap?.[value]?.find(item => item?.key === "sale_event")
                      const salesEventEntity = saleEventClient.SalesEvent
                      const endTs = salesEventEntity?.endDateTime
                      const isPastEvent = !!endTs && dayjs().isAfter(dayjs(endTs))
                      //not setting the past sale events in the form
                      form.setFieldsValue({
                        salesEvent: isPastEvent ? null : salesEventEntity?.id,
                        subofSubTask: subchildren?.key,
                        subLocation: subchildren?.children?.[0]?.key,
                      })
                    }
                  }}
                  disabled={isEditable}
                />
              </Form.Item>
            )}
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.subTask !== curr?.subTask}>
              {({ getFieldValue }) => {
                const subTask = getFieldValue("subTask")
                const renderStartEndTime = isRenderMeetingDateTime(subTask)
                return (
                  <>
                    <DateTimePicker
                      fieldNames={["completionDate", !!renderStartEndTime ? "meetingTime" : "completionTime"]}
                      fieldLabels={
                        !!renderStartEndTime
                          ? [t("Meeting Date"), t("Meeting Time")]
                          : [t("Completion Date"), t("Completion Time")]
                      }
                      required
                      rules={rules}
                      range={completionDateRange}
                      grouped={!renderStartEndTime}
                      isRangePicker={!!renderStartEndTime}
                      timePickerProps={{
                        enableAllTimesToday: true,
                      }}
                      datePickerProps={meetingDatePickerProps}
                    />
                    {subTask === "meeting_rescheduled" && (
                      <DateTimePicker
                        fieldNames={["newMeetingDate", "newMeetingTime"]}
                        fieldLabels={[t("Meeting Date"), t("Meeting Time")]}
                        required
                        rules={rules}
                        range={{
                          startDate: dayjs()
                            .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
                            .startOf("day"),
                        }}
                        grouped={false}
                        isRangePicker={true}
                        timePickerProps={{
                          enableAllTimesToday: true,
                          onChange: item => {
                            form.setFieldsValue({
                              deadLineTime: [dayjs(item?.[0]), dayjs(item?.[1])],
                              timeSlot: [dayjs(item?.[0]), dayjs(item?.[1])],
                            })
                          },
                        }}
                        datePickerProps={{
                          onChange: item => {
                            form.setFieldsValue({ deadLine: item, dateSlot: item })
                          },
                          disableFutureTime: true,
                        }}
                      />
                    )}
                  </>
                )
              }}
            </Form.Item>
            {!!taskSubChildren?.length && (
              <Form.Item
                name="subofSubTask"
                label={task === "pre_sale" ? t("Outcome") : t("Location")}
                rules={rules.concat({
                  validator: (_, value) => {
                    if (!value)
                      return Promise.reject(
                        `${task === "pre_sale" ? t("Outcome") : t("Location")} ${t("task is required")}`
                      )
                    return Promise.resolve()
                  },
                })}
                required={task === "pre_sale"}
              >
                <SelectInput
                  placeholder={task === "pre_sale" ? t("Select outcome") : t("Select location")}
                  options={taskSubChildren}
                  getOptionValue={option => option?.key}
                  onChange={(value, option) => {
                    form.resetFields(["attachment"])
                    if (!isSubTaskSalesEvent(taskSubChildren, value)) {
                      setSaleEventDateBounds(null)
                    }
                    setFieldsValue({
                      subofSubTask: value,
                      subLocation: option.children[0]?.key,
                      nextTask: option?.nextStatus?.key,
                    })
                    resetMeetingTimeToDefault(taskSubChildren, subTask, value)
                  }}
                  disabled={isEditable }
                />
              </Form.Item>
            )}
            {!isEditable && (
              <SubTaskAdditionalFields
                leadDetailInfo={leadDetailInfo}
                rules={rules}
                setFormValues={setFormValues}
                form={form}
              />
            )}
            {!!subLocations?.length && (
              <Form.Item name="subLocation" label={t("Sub Location")} required rules={rules}>
                <SelectInput
                  placeholder={t("Select sub location")}
                  getOptionValue={option => option?.key}
                  options={subLocations}
                  onChange={(value, option) => {
                    setFieldsValue({ subLocation: value })
                  }}
                  disabled={isEditable}
                />
              </Form.Item>
            )}
            {subofSubTask && isSubTaskSalesEvent(taskSubChildren, subofSubTask) && (
              <Form.Item name="salesEvent" label={t("Sales Event")} required rules={rules}>
                <SelectInput
                  placeholder={t("Select sales event")}
                  options={salesEvents?.saleEvents}
                  getOptionValue={option => option?.id ?? option?.key}
                  disabled={isEditable}
                  inputLoading={isFetchingsSaleEvents}
                  onChange={(value, option) => {
                    const startTs = option?.startTimeStamp 
                    const endTs = option?.endTimeStamp

                    if (isMeetingDoneAtSaleEvent) {
                      if (!startTs || !endTs) {
                        setSaleEventDateBounds(null)
                        return
                      }
                      const windowMeta = getSaleEventWindowMeta(startTs, endTs)
                      setSaleEventDateBounds(windowMeta)
                      const renderRange = isRenderMeetingDateTime(subTask)
                      if (!windowMeta.isMultiDay) {
                        setFieldsValue({
                          completionDate: dayjs(startTs),
                          ...(renderRange
                            ? { meetingTime: [dayjs(startTs), dayjs(endTs)] }
                            : { completionTime: [dayjs(startTs), dayjs(startTs)] }),
                        })
                      } else {
                        const firstDay = dayjs(startTs).startOf("day")
                        const [st, en] = getMeetingTimeForDayInEvent(firstDay, startTs, endTs)
                        setFieldsValue({
                          completionDate: firstDay,
                          ...(renderRange ? { meetingTime: [st, en] } : { completionTime: [st, st] }),
                        })
                      }
                    } else {
                      setSaleEventDateBounds(null)
                      setFieldsValue({
                        completionDate: dayjs(option?.startTimeStamp),
                        ...(!!isRenderMeetingDateTime(subTask)
                          ? { meetingTime: [dayjs(option?.startTimeStamp), dayjs(option?.endTimeStamp)] }
                          : { completionTime: [dayjs(option?.startTimeStamp), dayjs(option?.startTimeStamp)] }),
                      })
                    }
                  }}
                />
              </Form.Item>
            )}
            <Form.Item
              name="attachment"
              label={t("Attachments")}
              required={isAttachmentMandatory}
              rules={rules.concat({
                validator: async (_, value) => {
                  if (isAttachmentMandatory && (value?.length || 0) === 0) {
                    return Promise.reject(t("Attachment is required"))
                  }
                  return Promise.resolve()
                },
              })}
            >
              <MeetingVerificationAttachmentUploader
                name="attachment"
                files={attachment}
                setFiles={cb => {
                  form.setFieldsValue({ attachment: cb(attachment) })
                }}
                disabled={
                  (isEditable && leadDetailInfo?.isTaskStatusSales) ||
                  (isAttachmentVerificationRequired(subTask) && !meetingVerificationTaskTypeKey)
                }
                verifyMeeting={isAttachmentVerificationRequired(subTask)}
                taskType={meetingVerificationTaskTypeKey}
                setIsAttachmentValidation={setIsAttachmentValidation}
                imageType={
                  isPDFAllowed(subTask)
                    ? "application/pdf, image/png,image/jpeg, image/jpg"
                    : "image/png, image/jpeg, image/jpg"
                }
                maxCount={1}
              />
            </Form.Item>
            {!isEditable && (
              <Form.Item
                name="voiceComment"
                label={t("Add Voice Comment")}
                rules={[
                  {
                    validator: (_, _value) => {
                      if (recorderControls?.isRecording) {
                        return Promise.reject("Recording is still in progress, press the save button")
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <VoiceComment
                  getController={controller => (recorderControls = controller)}
                  setVoice={voice => {
                    setFieldsValue({ voiceComment: voice })
                  }}
                />
              </Form.Item>
            )}
            <Form.Item
              className={cx(!!leadDetailInfo?.suggestedComments?.length && styles.commentInput, "span-all")}
              name="comment"
              label={t("Comment")}
              rules={rules}
              required
              extra={renderSuggestedComments({
                suggestedComments: leadDetailInfo?.suggestedComments,
                setFieldsValue,
              })}
            >
              <TextInput
                placeholder={t("Enter comment")}
                maxLength={1000}
                lineCount={3}
                disabled={isEditable && leadDetailInfo?.isTaskStatusSales}
              />
            </Form.Item>
          </Group>
        </div>

        <NextTask
          isEditable={isEditable}
          busySlotVisible={busySlotVisible}
          setBusySlotVisible={setBusySlotVisible}
          rules={rules}
          lead={lead}
          leadDetailInfo={leadDetailInfo}
          taskTypes={taskTypes}
          form={form}
          formRef={formRef}
        />
      </Group>
    </Form>
  )
}
